import React, { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Hidden from '@mui/material/Hidden';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import AccountCircle from '@mui/icons-material/AccountCircle';
import AccountTree from '@mui/icons-material/AccountTree';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import Home from '@mui/icons-material/Home';
import GetApp from '@mui/icons-material/GetApp';
import MenuIcon from '@mui/icons-material/Menu';
import People from '@mui/icons-material/People';
import WatchLater from '@mui/icons-material/WatchLater';
import ListIcon from '@mui/icons-material/List';
import Language from '@mui/icons-material/Language';
import Cloud from '@mui/icons-material/Cloud';
import { LoadingContext } from '../contexts/LoadingContext';
import { Notifications } from '../components/Notifications';
import { DashboardGuardedDrawerItem } from './partials/DashboardDrawerItem';
import { useHistory } from 'react-router-dom';
import { MenuLink } from './partials/MenuLink';
import { useEnumState } from '../hooks/useEnumState';
import { config } from '../config';
import { combineCn } from '../helpers/combineCn';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { Licenses, ProductFilled } from '../components/Icon/Icon';
import MenuOpen from '@mui/icons-material/MenuOpen';
import { authenticatedRoot, AuthenticatedRoutes } from '../routers/AuthenticatedRoutes';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { AuthContext } from '../contexts/AuthContext';
import { useQueryUrl } from '../hooks/useQueryUrl';
import { useCloseDialog } from '../hooks/useCloseDialog';
import { ChooseOrganizationModal } from '../components/ChooseOrganizationModal/ChooseOrganizationModal';
import { useQueryParam } from '../hooks/useQueryParam';
import { CorporateFare, Description, Help, Mail, Public } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { GuestRoutes } from '../routers/GuestRouter';
import { ListItemButton, ListItemText } from '@mui/material';
import { AuthChangedExternally } from '../components/AuthChangedExternally/AuthChangedExternally';

const drawerWidth = 240;
const topBarHeight = 48;

const augmentWithOpacity = (color: string, opacity: number) => {
  if (color.startsWith('#')) {
    return (
      color +
      Math.round(opacity * 255)
        .toString(16)
        .padStart(2, '0')
    );
  } else if (color.startsWith('rgb(')) {
    return `rgba${color.substr(3, color.length - 4)}, ${opacity})`;
  }
};

const useStyles = makeStyles((theme) => {
  return createStyles({
    root: {
      display: 'flex',
      backgroundImage: `linear-gradient(45.05deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
    },
    appBar: {
      [theme.breakpoints.up('sm')]: {
        zIndex: theme.zIndex.drawer + 1,
      },
    },
    appBarBrand: {
      display: 'flex',
      alignSelf: 'center',
      flexGrow: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    appBarDivider: {
      width: 0,
      height: 16,
      borderLeft: `1px solid ${theme.palette.text.primary}`,
      opacity: 0.3,
      margin: '0 20px 0 4px',
    },
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
    },
    drawerPaper: {
      width: drawerWidth,
      background: theme.palette.background.paper,
    },
    mobileDrawerPaper: {
      minWidth: drawerWidth,
      width: 'calc(100vw - 64px)',
      backdropFilter: 'blur(4px) grayscale(1)',
      background: augmentWithOpacity(theme.palette.background.paper, 0.8),
      borderRight: `4px solid ${theme.palette.primary.dark}`,
    },
    content: {
      width: '100%',
      flexGrow: 1,
      marginTop: topBarHeight,
      padding: theme.spacing(3),
      minHeight: `calc(100vh - ${topBarHeight}px)`,
      [theme.breakpoints.up('sm')]: {
        width: `calc(100% - ${drawerWidth}px)`,
      },
    },
    backdrop: {
      zIndex: 9999,
    },
    drawerFiller: {
      backgroundColor: 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'right',
      overflow: 'hidden',
      ...theme.mixins.toolbar,
    },
    menuPaper: {
      marginTop: '18px',
    },
  });
});

export const DashboardLayout: FC = ({ children }) => {
  const styles = useStyles();
  const profileIconRef = useRef(null);
  const [isProfileMenuOpen, openProfileMenu, closeProfileMenu] = useEnumState(false, true, false);

  const { logOut } = useAuth();

  const { isLoading } = useContext(LoadingContext);
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const { identity, identities, setIdentity, changedExternally } = useContext(AuthContext);
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const [languagePicker] = useLocalStorage('languagePicker', false);
  const [language, setLanguage] = useLocalStorage('language', i18n.language);
  // CLOUD-1186: Disable 2FA tooltip
  // useEncourageMFA();

  const isOrganizationDialogOpen = useQueryParam('dialog') === 'organization';
  const chooseOrganizationUrl = useQueryUrl('dialog', 'organization');
  const closeDialog = useCloseDialog(void 0, ['dialog']);

  const { organization } = useContext(OrganizationContext);

  // Following effect seemed to be a good idea at the beginning, however then it happened to be quite annoying, since
  //   sometimes it blocks the inputs while typing.
  // useEffect(() => {
  //   if (isLoading) {
  //     const listener = (e: KeyboardEvent) => e.preventDefault();
  //     document.addEventListener('keydown', listener);
  //     return () => document.removeEventListener('keydown', listener);
  //   }
  // }, [isLoading]);

  const handleDrawerToggle = useCallback(() => {
    setDrawerExpanded(!drawerExpanded);
  }, [drawerExpanded]);

  useEffect(() => {
    return history.listen(() => {
      closeProfileMenu();
      setDrawerExpanded(false);
    });
  }, []);

  useEffect(() => {
    i18n.changeLanguage(language).then();
  }, [language]);

  const drawer = (
    <div>
      <div className={styles.drawerFiller}>
        <IconButton onClick={handleDrawerToggle}>
          <MenuOpen />
        </IconButton>
      </div>
      <List>
        {organization ? (
          <>
            <ListItemButton disabled={true}>
              <ListItemText>{organization.name}</ListItemText>
            </ListItemButton>
            <DashboardGuardedDrawerItem
              route={'CurrentOrganizationOverview'}
              label={t('layout.navigation.overview')}
              icon={Home}
              params={[]}
            />

            <DashboardGuardedDrawerItem
              route={'CurrentOrganizationUsers'}
              label={t('layout.navigation.organizationUsers')}
              icon={People}
              params={[]}
              match={[AuthenticatedRoutes.UserOrganization(organization.name)]}
            />

            <DashboardGuardedDrawerItem
              route={'CurrentOrganizationDetails'}
              label={t('layout.navigation.organizationDetails')}
              icon={CorporateFare}
              params={[]}
              match={[AuthenticatedRoutes.UserOrganization(organization.name)]}
            />

            <DashboardGuardedDrawerItem
              route={'CurrentOrganizationInvites'}
              label={t('layout.navigation.invites')}
              params={[]}
              icon={Mail}
            />

            <DashboardGuardedDrawerItem
              route={'CurrentOrganizationProducts'}
              label={t('layout.navigation.products')}
              icon={ProductFilled}
              params={[]}
            />

            <DashboardGuardedDrawerItem
              route={'Licenses'}
              label={t('layout.navigation.licenses')}
              icon={Licenses}
              params={[]}
            />

            <DashboardGuardedDrawerItem
              route={'AllLicenses'}
              label={t('layout.navigation.allLicenses')}
              icon={Licenses}
              params={[]}
            />

            <DashboardGuardedDrawerItem
              route={'CurrentOrganizationCloudLive'}
              label={t('layout.navigation.cloudLive')}
              params={[]}
              icon={Cloud}
            />
          </>
        ) : null}

        <DashboardGuardedDrawerItem
          route={'GlobalOverview'}
          label={t('layout.navigation.adminOverview')}
          icon={Home}
          params={[]}
        />
        <DashboardGuardedDrawerItem
          route={'UserDownloads'}
          label={t('layout.navigation.downloads')}
          params={[]}
          icon={GetApp}
        />

        <DashboardGuardedDrawerItem
          route={'Documentation'}
          label={t('layout.navigation.documentation')}
          icon={Description}
          params={[]}
        />

        <DashboardGuardedDrawerItem
          route="Admins"
          label={t('layout.navigation.admins')}
          icon={AdminPanelSettings}
          params={[]}
        />

        <DashboardGuardedDrawerItem
          route={'Organizations'}
          label={t('layout.navigation.organizations')}
          icon={CorporateFare}
          params={[]}
        />
        <DashboardGuardedDrawerItem route="AllUsers" label={t('layout.navigation.users')} icon={People} params={[]} />

        <DashboardGuardedDrawerItem
          route={'AdminProducts'}
          label={t('layout.navigation.adminProducts')}
          icon={ProductFilled}
          params={[]}
        />

        <DashboardGuardedDrawerItem
          route={'Releases'}
          label={t('layout.navigation.releases')}
          icon={AccountTree}
          params={[]}
        />

        <DashboardGuardedDrawerItem route={'Support'} label={t('layout.navigation.support')} icon={Help} params={[]} />

        <DashboardGuardedDrawerItem route="Jobs" label={t('layout.navigation.tasks')} icon={WatchLater} params={[]} />
        <DashboardGuardedDrawerItem
          route="Activity"
          label={t('layout.navigation.activity')}
          icon={Public}
          params={[]}
        />

        <DashboardGuardedDrawerItem
          route={'CloudLive'}
          label={t('layout.navigation.cloudLive')}
          icon={Cloud}
          params={[]}
        />

        <DashboardGuardedDrawerItem
          route={'Events'}
          label={t('layout.navigation.events')}
          icon={ListIcon}
          params={[]}
        />
      </List>

      {(() => false)() ? (
        <List>
          {/* USER */}

          {/*<DashboardDrawerItem route={UserRoutes.Support} label={t('layout.navigation.support')} icon={Help} />*/}
          {/*{config.enableLive ? (*/}
          {/*  <DashboardDrawerItem*/}
          {/*    route={UserRoutes.LivePreview}*/}
          {/*    label={t('layout.navigation.live')}*/}
          {/*    icon={PhotoCamera}*/}
          {/*  />*/}
          {/*) : null}*/}
          {/* CUSTOMER */}
          {/*{config.enableInvoices ? (*/}
          {/*  <DashboardDrawerItem*/}
          {/*    route={CustomerRoutes.Invoices}*/}
          {/*    label={t('layout.navigation.invoices')}*/}
          {/*    icon={Payment}*/}
          {/*  />*/}
          {/*) : null}*/}
          {/*<DashboardDrawerItem route={CustomerRoutes.Support} label={t('layout.navigation.support')} icon={Help} />*/}
          {/* ADMIN */}
          {/*<DashboardAdminDrawerItem route="Support" label={t('layout.navigation.support')} icon={Help} />*/}
          {/*<DashboardAdminDrawerItem route="Balance" label={t('layout.navigation.balance')} icon={Timeline} />*/}
        </List>
      ) : null}
    </div>
  );

  return (
    <div className={styles.root}>
      <CssBaseline />
      <AppBar position="fixed" className={styles.appBar}>
        <Toolbar>
          <Hidden smUp>
            <IconButton color="inherit" onClick={handleDrawerToggle} size="large">
              <MenuIcon />
            </IconButton>
          </Hidden>
          <div className={styles.appBarBrand}>
            <img src={config.pixotopeLogo} />
            <Hidden smDown>
              <div className={styles.appBarDivider} />
              <img src={config.cloudLogo} />
            </Hidden>
          </div>
          <div onClick={openProfileMenu} ref={profileIconRef}>
            <IconButton color="inherit" size="large">
              <AccountCircle />
            </IconButton>
          </div>
        </Toolbar>

        <Menu
          anchorEl={profileIconRef.current}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          keepMounted
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={isProfileMenuOpen}
          onClose={closeProfileMenu}
          classes={{
            paper: styles.menuPaper,
          }}
        >
          {identity ? <MenuItem disabled>{identity.email}</MenuItem> : null}

          <MenuLink to={chooseOrganizationUrl}>{t('layout.accountItems.organization') as string}</MenuLink>

          <MenuLink to={AuthenticatedRoutes.ViewAccount()}>{t('layout.accountItems.viewAccount') as string}</MenuLink>

          {languagePicker && i18n.language !== 'en' ? (
            <MenuItem onClick={() => setLanguage('en')}>
              <Language /> English
            </MenuItem>
          ) : null}
          {languagePicker && i18n.language !== 'pl' ? (
            <MenuItem onClick={() => setLanguage('pl')}>
              <Language /> Polski
            </MenuItem>
          ) : null}

          <Divider />
          <MenuItem
            onClick={() => {
              history.push(GuestRoutes.SignIn());
            }}
          >
            Log in to different account
          </MenuItem>
          {identities
            .filter(({ email }) => email !== identity?.email)
            .map((identity) => (
              <MenuItem
                key={identity.email}
                onClick={() => {
                  setIdentity(identity);
                  history.push(authenticatedRoot);
                }}
              >
                Switch to {identity.email}
              </MenuItem>
            ))}

          <Divider />
          <MenuItem onClick={logOut}>{t('layout.accountItems.logOut')}</MenuItem>
        </Menu>
      </AppBar>

      <Hidden smUp implementation="css">
        <Drawer
          variant="temporary"
          anchor="left"
          className={styles.drawer}
          classes={{
            paper: combineCn({ [styles.drawerPaper]: true, [styles.mobileDrawerPaper]: true }),
          }}
          open={drawerExpanded}
          onClose={handleDrawerToggle}
        >
          {drawer}
        </Drawer>
      </Hidden>
      <Hidden smDown implementation="css">
        <Drawer
          variant="permanent"
          anchor="left"
          className={styles.drawer}
          classes={{
            paper: styles.drawerPaper,
          }}
        >
          {drawer}
        </Drawer>
      </Hidden>

      <ChooseOrganizationModal open={isOrganizationDialogOpen} onClose={closeDialog} />

      <main className={styles.content}>{children || ''}</main>

      <Notifications />
      <Backdrop open={isLoading} className={styles.backdrop} data-cy={isLoading ? 'global-loading' : void 0}>
        <CircularProgress />
      </Backdrop>
      <AuthChangedExternally isOpen={changedExternally} />
    </div>
  );
};
